import React from "react";
import Image from "components/image";
import RichText from "components/rich-text";
import ButtonLink from "components/button-link";
import { Swiper, SwiperSlide } from "components/swiper";
import { Navigation, Mousewheel, Autoplay, Pagination } from "swiper/modules";
import { ReactComponent as Arrow } from "svgs/middle-size-arrow.svg";
import clsx from "clsx/lite";

const RowDirection = ({ item, index }) => (
	<div className={clsx("flex w-full flex-col lg:flex-row bg-white md:h-auto h-fit",
		item.imageLeft ? "lg:flex-row-reverse" : "lg:flex-row")}>
		<div className="flex-1 flex flex-col justify-center p-4 md:p-10">
			<div className="w-full items-center gap-2">
				<div className="bg-black text-white text-2xl font-bold w-fit px-2 mb-4"> {index + 1}</div>
				{item.title && (
					<h4 className="text-black text-2xl md:text-h1-xs/10 font-base">
						{item.title}
					</h4>
				)}
				{item.description && (
					<div className="flex flex-col items-center justify-center pt-4 line-clamp-4">
						<RichText data={{ content: item.description }} />
					</div>
				)}
			</div>
		</div>
		<div
			className={clsx("flex-1 flex items-center justify-center p-4 lg:pl-0 lg:py-10",
				item.imageLeft ? "lg:pl-10" : "lg:pr-10",
				item.extraBottomPadding ? "pb-16" : "pb-6")}>
			{item?.image && (
				<div className="h-80 lg:w-full">
					<Image image={item.image} className="h-full object-contain lg:[&>img]:!object-contain" />
				</div>
			)}
		</div>
	</div>
);

const ColumnDirection = ({ item }) => (
	<div className="flex justify-center h-auto max-w-screen-lg">
		<div className="flex w-full flex-col lg:flex-col bg-white md:h-auto h-fit">
			<div className="flex-1 flex flex-col gap-5">
				{item.image &&
					<Image image={item.image} className="w-full h-fit overflow-hidden" />
				}
				<div className="w-full items-center gap-2">
					{item.title && <p className="text-lg font-semibold">{item.title}</p>}
					{item.description &&
						<div className="flex flex-col items-center justify-center pt-4 line-clamp-4 *:text-secondary">
							<RichText data={{ content: item.description }} />
						</div>
					}
				</div>
			</div>
		</div>
	</div>
);

const InfoCarousel = ({ data }) => {
	const isFluid = data.settings?.isFluid;
	const items = data.items;

	const { direction } = data;
	const columnDirection = direction === "column";

	const swiperSettings = {
		modules: [Navigation, Mousewheel, Autoplay, Pagination],
		loop: items.length > 3,
		speed: 2000,
		centeredSlides: !columnDirection,
		mousewheel: { enabled: true, forceToAxis: true, },
		navigation: { nextEl: ".right-arrow", prevEl: ".left-arrow", disabledClass: "opacity-50", },
		pagination: data.hideDots ? false : { el: ".swiper-pagination-info-carousel", clickable: true, },
		slidesPerView: 1.5,
		spaceBetween: 40,
		breakpoints: {
			320: { slidesPerView: columnDirection ? 1.1 : 1, spaceBetween: 10 },
			680: { slidesPerView: 1.5, spaceBetween: 30 },
			992: { slidesPerView: 1.5, spaceBetween: columnDirection ? "10" : "40" },
			1900: { slidesPerView: "auto", spaceBetween: columnDirection ? "10" : "40" }
		},
	};

	return (
		<div className="flex flex-col items-center">
			<div>
				{data.title && (
					<h2 className={clsx(
						"pb-4 font-base",
						isFluid && "standard-section-width horizontal-padding mx-auto",
						data.leftAlign ? "text-left !pl-8 leading-8 md:leading-tight" : "md:text-center",
						columnDirection && "!pb-14"
					)}>
						{data.title}
					</h2>
				)}
				{data.subtitle && (
					<p className={
						clsx(
							"pb-10 text-sm md:text-base w-full",
							isFluid && "standard-section-width horizontal-padding mx-auto",
							data.leftAlign ? "text-left text-darkGray !pl-8" : "text-center sm:pb-20"
						)}
					>
						{data.subtitle}
					</p>
				)}
			</div>
			<div className="w-full">
				<Swiper {...swiperSettings} className="flex w-full overflow-auto no-scrollbar">
					{items.map((item, index) => (
						<SwiperSlide key={index}
							className={clsx(
								"flex justify-center h-auto max-w-screen-lg",
								columnDirection ? "" : "lg:mx-4",
							)}>
							{columnDirection
								? <ColumnDirection item={item} />
								: <RowDirection item={item} index={index} />}
						</SwiperSlide>
					))}
				</Swiper>
			</div>
			<div className={clsx("flex justify-center items-center space-x-3 mt-5", columnDirection && "pt-9")}>
				<button className={
					clsx("left-arrow cursor-pointer border border-black p-1.5 bg-black",
						!columnDirection && "rounded-full")}>
					<Arrow className="rotate-180 invert" />
				</button>
				{!data.hideDots && <div className="swiper-pagination-info-carousel relative mt-2"></div>}
				<button className={
					clsx("right-arrow cursor-pointer border border-black p-1.5 bg-black",
						!columnDirection && "rounded-full")}>
					<Arrow className="invert" />
				</button>
			</div>
			<div className={clsx("flex pt-6", isFluid && "standard-section-width horizontal-padding mx-auto")}>
				<ButtonLink data={data?.cta} className="w-full sm:w-auto font-bold" />
			</div>
		</div>
	);
};

export default InfoCarousel;